/**
 * Aparência de uma embarcação no mapa — funções puras (testadas em
 * `look.test.ts`).
 *
 * Três formas, e cada uma responde a uma pergunta diferente:
 *
 * | Forma | Quando | Diz |
 * |---|---|---|
 * | **seta** | navegando (≥ 0,5 nó) | para onde está indo |
 * | **losango** | parado, fundeado ou atracado | está aqui, sem direção |
 * | **contorno tracejado** | fora do alcance há mais de 1 h | esta é a ÚLTIMA posição conhecida, não a de agora |
 *
 * Parado não pode virar seta: o rumo de quem está a 0,1 nó é ruído do GPS, e
 * apontar uma seta para ele inventaria uma intenção que o dado não tem.
 *
 * A cor é da família (carga, tanque, passageiros…), nunca da velocidade: a
 * velocidade já está na forma, e duas variáveis na mesma tinta não se leem.
 *
 * O símbolo é desenhado SEMPRE apontando ao norte, e a rotação entra no
 * `Style`, não no sprite. Assim a chave do cache de sprites tem no máximo
 * `famílias × estados` entradas (24), em vez de uma por rumo possível.
 */
import type { Familia } from './kinds';

/** Abaixo disso, a embarcação está parada para efeito de desenho. */
export const PARADO_NO = 0.5;

/** Sem ser ouvida por mais que isso, a posição deixa de valer como "agora". */
export const VELHO_MS = 60 * 60 * 1000;

/**
 * Cores por família. Escolhidas para se distinguirem sobre imagem de satélite
 * (nuvem branca e mata escura) e para não colidirem com o laranja do fogo nem
 * com o azul da chuva, que já significam outra coisa no portal.
 */
export const COR_FAMILIA: Readonly<Record<Familia, string>> = {
  carga: '#f4d35e',
  tanque: '#e07a5f',
  passageiros: '#8ecae6',
  pesca: '#90be6d',
  reboque: '#c77dff',
  servico: '#a8dadc',
  recreio: '#d0d0d0',
  outro: '#b8b8b8',
};

export type Estado = 'navegando' | 'parado' | 'velho';

export function estadoDe(sog: number | null, visto: number, sumiu: boolean, agora: number = Date.now()): Estado {
  if (sumiu || agora - visto > VELHO_MS) return 'velho';
  return sog !== null && sog >= PARADO_NO ? 'navegando' : 'parado';
}

export type Forma = 'seta' | 'losango';

export interface Visual {
  forma: Forma;
  estado: Estado;
  /** Tamanho do símbolo em px (meia-altura da seta / meia-diagonal do losango). */
  raio: number;
  preenchimento: string | null;
  contorno: string;
  larguraContorno: number;
  tracejado: readonly number[];
  opacidade: number;
  /** Rotação a aplicar, em RADIANOS, já quantizada. `0` para o losango. */
  rotacao: number;
}

/**
 * Rumo quantizado em passos de 5°.
 *
 * O olho não distingue 87° de 88° num símbolo de 10 px, mas o cache de
 * estilos distingue — sem quantizar, um rumo contínuo geraria um objeto
 * `Style` novo por embarcação e por quadro, para sempre.
 */
export const PASSO_RUMO = 5;

export function quantizarRumo(graus: number): number {
  const g = ((graus % 360) + 360) % 360;
  return (Math.round(g / PASSO_RUMO) * PASSO_RUMO) % 360;
}

/**
 * O rumo a usar no desenho. Prefere a proa (`heading`, para onde o casco
 * aponta) e cai no rumo sobre o fundo (`cog`) — a proa costuma faltar.
 * Devolve `null` quando nenhum dos dois serve.
 */
export function rumoDe(heading: number | null, cog: number | null): number | null {
  // 511 é o "não disponível" da proa na norma do AIS.
  if (heading !== null && Number.isFinite(heading) && heading >= 0 && heading < 360) return heading;
  if (cog !== null && Number.isFinite(cog) && cog >= 0 && cog < 360) return cog;
  return null;
}

export function visualDe(
  familia: Familia,
  estado: Estado,
  heading: number | null,
  cog: number | null,
  tamanho: number,
  opacidade: number,
): Visual {
  const cor = COR_FAMILIA[familia];
  const rumo = rumoDe(heading, cog);

  // Sem rumo confiável, nem navegando: não há seta a desenhar.
  const forma: Forma = estado === 'navegando' && rumo !== null ? 'seta' : 'losango';
  const velho = estado === 'velho';

  return {
    forma,
    estado,
    raio: tamanho,
    // A embarcação fora de alcance fica vazada: a diferença entre "está aqui"
    // e "estava aqui" tem de ser visível sem clicar.
    preenchimento: velho ? null : cor,
    contorno: velho ? cor : 'rgba(10,16,28,0.85)',
    larguraContorno: velho ? 1.4 : 1,
    tracejado: velho ? [2.2, 1.8] : [],
    opacidade: velho ? opacidade * 0.7 : opacidade,
    rotacao: forma === 'seta' && rumo !== null ? (quantizarRumo(rumo) * Math.PI) / 180 : 0,
  };
}

/**
 * Chave do cache de sprites. NÃO inclui o rumo — ele entra na rotação do
 * `Style`, e é isso que mantém o cache com dezenas de entradas em vez de
 * milhares.
 */
export function chaveSprite(v: Visual, dpr: number): string {
  return [
    dpr.toFixed(2),
    v.forma,
    v.estado,
    v.raio.toFixed(2),
    v.preenchimento ?? '-',
    v.contorno,
    v.larguraContorno,
    v.tracejado.join(','),
    v.opacidade.toFixed(3),
  ].join('|');
}

/**
 * Situação de navegação declarada (campo `nav_status` do AIS), em português.
 * Só os códigos que a norma define; o resto vira `null` e a interface omite.
 */
export const NAV_STATUS: Readonly<Record<number, string>> = {
  0: 'a caminho, com motor',
  1: 'fundeada',
  2: 'sem governo',
  3: 'manobra restrita',
  4: 'restrita pelo calado',
  5: 'atracada',
  6: 'encalhada',
  7: 'pescando',
  8: 'a caminho, à vela',
  11: 'rebocada por trás',
  12: 'empurrando ou rebocada ao lado',
  14: 'transmitindo alerta (AIS-SART)',
};

export function situacaoDe(navStatus: number | null): string | null {
  if (navStatus === null || !Number.isFinite(navStatus)) return null;
  return NAV_STATUS[navStatus] ?? null;
}
